import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { track } from '@theme/r-analytics';

// Bump the version suffix whenever the worker's response shape or filtering
// changes — every browser will then treat the old entry as nonexistent and
// fall through to a fresh fetch on the next drawer open.
const STORES_CACHE_KEY = 'rmsc_locator_stores_v3';      // v3: PDP hits ?location_type=rocky_store explicitly
const INVENTORY_CACHE_KEY = 'rmsc_locator_inventory_v1';
const STORES_TTL_MS = 60 * 60 * 1000;     // 1 h — store list is near-static
const INVENTORY_TTL_MS = 5 * 60 * 1000;   // 5 min — matches Worker edge cache
const VARIANT_DEBOUNCE_MS = 300;

/**
 * Stock thresholds preserved verbatim from `inventory-by-location.liquid`
 * (1.9 kg soap slabs vs everything else).
 *
 * @param {number} qty
 * @param {string} variantTitle
 * @returns {{ status: 'in' | 'low' | 'none' }}
 */
function classifyStock(qty, variantTitle) {
  if (variantTitle === '1.9kg') {
    if (qty > 1) return { status: 'in' };
    if (qty === 1) return { status: 'low' };
    return { status: 'none' };
  }
  if (qty > 10) return { status: 'in' };
  if (qty > 3) return { status: 'low' };
  return { status: 'none' };
}

/**
 * sessionStorage helpers — silently degrade when storage isn't available
 * (private browsing, quota errors).
 */
function readJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (ch) => (
    ch === '&' ? '&amp;' :
    ch === '<' ? '&lt;'  :
    ch === '>' ? '&gt;'  :
    ch === '"' ? '&quot;' :
    '&#39;'
  ));
}

class RPdpFindInStore extends Component {
  requiredRefs = ['list'];

  #abort = new AbortController();
  #variantTitles = {};
  #currentVariantId = null;
  #currentVariantTitle = '';
  #stores = null;
  #fetchedVariants = new Set();
  #variantDebounce = null;
  #i18n = {
    loading:        'Checking our stores…',
    error:          'We couldn\'t reach our stores just now. Please try again in a moment.',
    inStock:        'In stock',
    lowStock:       'Low stock — call ahead',
    noStock:        'Out of stock',
    unknownStock:   'Call to check',
    summary:        'In stock at __COUNT__ of __TOTAL__ stores',
    summaryNone:    'Not in stock at any store right now',
    summaryUnknown: 'Live stock is unavailable — call the store to check',
    directions:     'Directions',
  };

  connectedCallback() {
    super.connectedCallback();

    const idAttr = this.dataset.initialVariantId;
    this.#currentVariantId = idAttr ? parseInt(idAttr, 10) : null;
    this.#currentVariantTitle = this.dataset.initialVariantTitle || '';

    try {
      this.#variantTitles = JSON.parse(this.dataset.variantTitles || '{}');
    } catch {
      this.#variantTitles = {};
    }

    // Localised copy: the loading/error strings come from the server-rendered
    // placeholders, the row/summary strings from data-i18n-* on the host.
    if (this.refs.loading?.textContent) {
      this.#i18n.loading = this.refs.loading.textContent.trim();
    }
    const d = this.dataset;
    this.#i18n.inStock        = d.i18nInStock        || this.#i18n.inStock;
    this.#i18n.lowStock       = d.i18nLowStock       || this.#i18n.lowStock;
    this.#i18n.noStock        = d.i18nNoStock        || this.#i18n.noStock;
    this.#i18n.unknownStock   = d.i18nUnknownStock   || this.#i18n.unknownStock;
    this.#i18n.summary        = d.i18nSummary        || this.#i18n.summary;
    this.#i18n.summaryNone    = d.i18nSummaryNone    || this.#i18n.summaryNone;
    this.#i18n.summaryUnknown = d.i18nSummaryUnknown || this.#i18n.summaryUnknown;
    this.#i18n.directions     = d.i18nDirections     || this.#i18n.directions;

    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // The element now lives inside the Rocky side drawer (r-side-drawer.liquid),
    // so the lazy-fetch trigger is dialog.js's DialogOpenEvent instead of the old
    // accordion `toggle`. That event does not bubble, so listen on the
    // <dialog-component> itself.
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent) {
      dialogComponent.addEventListener('dialog:open', this.#handleDialogOpen, { signal });
      // Guard against being connected while the drawer is already open (morph).
      if (this.#isPanelOpen()) this.#refresh();
    } else {
      // Rendered outside a drawer — just fetch immediately.
      this.#refresh();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    if (this.#variantDebounce) clearTimeout(this.#variantDebounce);
    super.disconnectedCallback();
  }

  // ── event handlers ──────────────────────────────────────────────────────────

  /** True when the enclosing drawer panel is showing (or there is no drawer). */
  #isPanelOpen() {
    const dialog = this.closest('dialog');
    return dialog ? dialog.open : true;
  }

  #handleDialogOpen = () => {
    track('store_locator', { action: 'find_in_store_open', product_id: this.dataset.productId });
    if (this.#currentVariantId && !this.#fetchedVariants.has(this.#currentVariantId)) {
      this.#refresh();
    }
  };

  #handleVariantUpdate = (event) => {
    const variant = event?.detail?.resource;
    if (!variant?.id) return;
    const id = Number(variant.id);
    if (!Number.isFinite(id) || id === this.#currentVariantId) return;

    const title = variant.title || this.#variantTitles[id] || '';

    this.#currentVariantId = id;
    this.#currentVariantTitle = title;
    this.#renderVariantLabel();

    if (this.#variantDebounce) clearTimeout(this.#variantDebounce);
    this.#variantDebounce = setTimeout(() => {
      if (this.#isPanelOpen()) {
        this.#refresh();
      } else if (!this.#fetchedVariants.has(id)) {
        this.#renderLoading();
      }
    }, VARIANT_DEBOUNCE_MS);
  };

  // ── data ────────────────────────────────────────────────────────────────────

  async #refresh() {
    if (this.dataset.featureEnabled === 'false') return;
    if (!this.dataset.workerUrl) {
      console.warn('[r-pdp-find-in-store] No worker URL configured.');
      return;
    }
    if (!this.#currentVariantId) return;

    const variantId = this.#currentVariantId;
    this.#renderLoading();

    try {
      // The store list is the hard dependency; inventory is best-effort. If the
      // stock call fails the stores still render, with "Call to check" in place
      // of a status — the addresses and phone numbers are the useful half.
      const [stores, inventory] = await Promise.all([
        this.#fetchStores(),
        this.#fetchInventory(variantId).catch((err) => {
          console.warn('[r-pdp-find-in-store] inventory unavailable', err);
          return null;
        }),
      ]);

      // A variant change landed while we were waiting — that refresh wins.
      if (variantId !== this.#currentVariantId) return;

      if (!stores?.length) {
        this.#renderError();
        return;
      }

      let map = null;
      if (inventory) {
        map = {};
        for (const row of inventory) {
          if (row?.locationId != null) map[row.locationId] = Number(row.available) || 0;
        }
        this.#fetchedVariants.add(variantId);
      }

      this.#renderStores(stores, map);
    } catch (err) {
      console.error('[r-pdp-find-in-store]', err);
      this.#renderError();
    }
  }

  async #fetchStores() {
    if (this.#stores) return this.#stores;

    const cached = readJson(STORES_CACHE_KEY);
    if (cached && Date.now() - cached.ts < STORES_TTL_MS && Array.isArray(cached.stores)) {
      this.#stores = cached.stores;
      return cached.stores;
    }

    // PDP find-in-store only ever cares about the 14 active retail stores —
    // ask the worker to push the filter to Shopify so we never receive the
    // wholesale / archived metaobjects over the wire.
    const url = new URL('stores', this.#normaliseWorkerUrl());
    url.searchParams.set('location_type', 'rocky_store');
    const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`stores HTTP ${res.status}`);
    const body = await res.json();
    if (!body?.ok) throw new Error('stores not ok');

    this.#stores = body.stores ?? [];
    writeJson(STORES_CACHE_KEY, { ts: Date.now(), stores: this.#stores });
    return this.#stores;
  }

  async #fetchInventory(variantId) {
    const cache = readJson(INVENTORY_CACHE_KEY) ?? {};
    const entry = cache[variantId];
    if (entry && Date.now() - entry.ts < INVENTORY_TTL_MS && Array.isArray(entry.inventory)) {
      return entry.inventory;
    }

    const url = new URL('inventory', this.#normaliseWorkerUrl());
    url.searchParams.set('variantId', String(variantId));
    const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`inventory HTTP ${res.status}`);
    const body = await res.json();
    if (!body?.ok) throw new Error('inventory not ok');

    const inventory = body.inventory ?? [];
    cache[variantId] = { ts: Date.now(), inventory };
    writeJson(INVENTORY_CACHE_KEY, cache);
    return inventory;
  }

  /** URL constructor needs a trailing slash to treat the value as a base. */
  #normaliseWorkerUrl() {
    const raw = String(this.dataset.workerUrl || '').trim();
    if (!raw) throw new Error('worker url missing');
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  // ── rendering ───────────────────────────────────────────────────────────────

  #renderVariantLabel() {
    const el = this.refs.variantLabel;
    if (!el || el.hidden) return;
    el.textContent = this.#currentVariantTitle;
  }

  #setSummary(text) {
    const el = this.refs.summary;
    if (el) el.textContent = text;
  }

  #renderLoading() {
    const list = this.refs.list;
    if (!list) return;
    list.classList.add('is-loading');
    list.setAttribute('aria-busy', 'true');
    // Three ghost rows shaped like a real store row; the live text lives in the
    // role="status" summary so the skeleton itself stays decorative.
    list.innerHTML = Array.from({ length: 3 }, () => (
      '<li class="r-pdp-find-in-store__skeleton" aria-hidden="true">' +
        '<span class="r-pdp-find-in-store__skeleton-bar r-pdp-find-in-store__skeleton-bar--name"></span>' +
        '<span class="r-pdp-find-in-store__skeleton-bar r-pdp-find-in-store__skeleton-bar--stock"></span>' +
        '<span class="r-pdp-find-in-store__skeleton-bar r-pdp-find-in-store__skeleton-bar--address"></span>' +
      '</li>'
    )).join('');
    this.#setSummary(this.#i18n.loading);
    if (this.refs.error) this.refs.error.hidden = true;
  }

  #renderError() {
    const list = this.refs.list;
    if (!list) return;
    list.classList.remove('is-loading');
    list.setAttribute('aria-busy', 'false');
    list.innerHTML = '';
    this.#setSummary('');
    if (this.refs.error) this.refs.error.hidden = false;
  }

  /**
   * @param {Array<Record<string, any>>} stores
   * @param {Record<number, number> | null} inventoryMap - null when live stock could not be read
   */
  #renderStores(stores, inventoryMap) {
    const list = this.refs.list;
    if (!list) return;
    list.classList.remove('is-loading');
    list.setAttribute('aria-busy', 'false');
    if (this.refs.error) this.refs.error.hidden = true;

    const variantTitle = this.#currentVariantTitle;
    let available = 0;

    const rows = stores.map((store) => {
      const locId = Number(store.shopify_location_id);
      let status = 'unknown';
      if (inventoryMap) {
        const qty = Number.isFinite(locId) ? (inventoryMap[locId] ?? 0) : 0;
        status = classifyStock(qty, variantTitle).status;
        if (status !== 'none') available += 1;
      }

      const stockText =
        status === 'in'  ? this.#i18n.inStock  :
        status === 'low' ? this.#i18n.lowStock :
        status === 'none' ? this.#i18n.noStock :
        this.#i18n.unknownStock;

      const streetAddress = store.address || '';
      const locality = [store.city, store.province].filter(Boolean).join(', ');
      const fullAddress = [streetAddress, locality].filter(Boolean).join(', ');
      const mapsUrl = store.maps_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
      const tel = store.phone || '';
      const telDigits = tel.replace(/[^0-9+]/g, '');
      // WS23 — r-analytics.js publishes rocky_store_locator from these on click.
      const trackAttrs =
        `data-r-track="store_locator" data-r-track-store-id="${escapeHtml(store.handle ?? store.shopify_location_id)}" ` +
        `data-r-track-store-name="${escapeHtml(store.name)}" data-r-track-product-id="${escapeHtml(this.dataset.productId)}"`;

      return `
        <li class="r-pdp-find-in-store__store" data-status="${status}" data-location-id="${escapeHtml(store.shopify_location_id)}">
          <div class="r-pdp-find-in-store__store-main">
            <span class="r-pdp-find-in-store__store-name">${escapeHtml(store.name)}</span>
            <span class="r-pdp-find-in-store__store-address">
              ${escapeHtml(streetAddress)}${streetAddress && locality ? '<span class="r-pdp-find-in-store__store-address-sep" aria-hidden="true"> · </span>' : ''}${escapeHtml(locality)}
            </span>
          </div>
          <span class="r-pdp-find-in-store__stock r-pdp-find-in-store__stock--${status}">
            <span class="r-pdp-find-in-store__stock-dot" aria-hidden="true"></span>
            ${escapeHtml(stockText)}
          </span>
          <div class="r-pdp-find-in-store__store-actions">
            <a class="r-pdp-find-in-store__store-link" href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener" ${trackAttrs} data-r-track-action="directions">
              ${escapeHtml(this.#i18n.directions)}
            </a>
            ${tel ? `<a class="r-pdp-find-in-store__store-link" href="tel:${escapeHtml(telDigits)}" ${trackAttrs} data-r-track-action="call">${escapeHtml(tel)}</a>` : ''}
          </div>
        </li>
      `;
    }).join('');

    list.innerHTML = rows;
    this.#renderVariantLabel();

    if (!inventoryMap) {
      this.#setSummary(this.#i18n.summaryUnknown);
    } else if (available === 0) {
      this.#setSummary(this.#i18n.summaryNone);
    } else {
      this.#setSummary(
        this.#i18n.summary
          .replace('__COUNT__', String(available))
          .replace('__TOTAL__', String(stores.length)),
      );
    }
  }
}

if (!customElements.get('r-pdp-find-in-store')) {
  customElements.define('r-pdp-find-in-store', RPdpFindInStore);
}
